import { NetworkStatus, gql, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import * as React from 'react';

import { useQueryWithPastDataDeprecated } from '@/imports/ui/useQueryWithPastData';

import {
  MoveLibraryFolderModal_libraryFolder,
  MoveLibraryFolderModal_libraryFolderVariables,
} from './graphql-types/MoveLibraryFolderModal_libraryFolder';
import {
  MoveLibraryFolderModal_moveGroupLibraryFolder,
  MoveLibraryFolderModal_moveGroupLibraryFolderVariables,
} from './graphql-types/MoveLibraryFolderModal_moveGroupLibraryFolder';

import {
  MOVE_ITEMS_MODAL_LIBRARY_FOLDER_FRAGMENT,
  MoveItemsModal,
} from './MoveItemsModal';

const GET_LIBRARY_FOLDER_AST = gql`
  query MoveLibraryFolderModal_libraryFolder(
    $input: SingleLibraryFolderInputV2!
  ) {
    libraryFolderV2(input: $input) {
      result {
        _id
        ...MoveItemsModal_LibraryFolderFragment
        subFolders {
          results {
            _id
            ...MoveItemsModal_LibraryFolderFragment
          }
        }
      }
    }
  }
  ${MOVE_ITEMS_MODAL_LIBRARY_FOLDER_FRAGMENT}
`;

const MOVE_LIBRARY_FOLDER_AST = gql`
  mutation MoveLibraryFolderModal_moveGroupLibraryFolder(
    $input: MoveGroupLibraryFolderInput!
  ) {
    moveGroupLibraryFolder(input: $input)
  }
`;

type IMoveLibraryFolderModalProps = {
  rootFolderId: string;
  libraryFolderId: string;
  open: boolean;
  onClose: () => void;
  onAfterMove: () => void;
};

export const MoveLibraryFolderModal: React.FC<IMoveLibraryFolderModalProps> = ({
  rootFolderId,
  libraryFolderId,
  open,
  onClose,
  onAfterMove,
}) => {
  const toast = useToast();
  const [currentFolderId, setCurrentFolderId] =
    React.useState<string>(rootFolderId);
  const { error, data, networkStatus } = useQueryWithPastDataDeprecated<
    MoveLibraryFolderModal_libraryFolder,
    MoveLibraryFolderModal_libraryFolderVariables
  >(GET_LIBRARY_FOLDER_AST, {
    variables: {
      input: {
        id: currentFolderId,
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [moveFolderFunc, { loading: loadingSubmit }] = useMutation<
    MoveLibraryFolderModal_moveGroupLibraryFolder,
    MoveLibraryFolderModal_moveGroupLibraryFolderVariables
  >(MOVE_LIBRARY_FOLDER_AST, {
    awaitRefetchQueries: true,
    refetchQueries: ['GroupResourcesPane_LibraryFolderContents'],
  });

  React.useEffect(() => {
    setCurrentFolderId(rootFolderId);
  }, [rootFolderId]);

  const onSubmitMoveHandler = async (
    destinationFolderId: string
  ): Promise<void> => {
    try {
      await moveFolderFunc({
        variables: {
          input: {
            libraryFolderId: libraryFolderId,
            toLibraryFolderId: destinationFolderId,
          },
        },
      });
      onAfterMove();
      onClose();
      toast({ status: 'success', description: 'Moved folder.' });
    } catch (err) {
      toast({ status: 'error', description: 'Failed to move folder.' });
    }
  };

  if (error) {
    throw new Error('error getting folders');
  }

  const currentFolder = !!data?.libraryFolderV2?.result
    ? data.libraryFolderV2.result
    : undefined;
  if (currentFolder && currentFolder._id === libraryFolderId) {
    throw new Error('Cannot move to library folder being moved.');
  }
  const subFolders = !!currentFolder ? currentFolder.subFolders.results : [];
  const loadingFolders =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables;
  return (
    <MoveItemsModal
      disableFolderId={libraryFolderId}
      subFolders={subFolders}
      open={open}
      rootFolderId={rootFolderId}
      currentFolder={currentFolder}
      loadingFolders={loadingFolders}
      loadingSubmit={loadingSubmit}
      onClickCancel={onClose}
      onClickNavigateToFolder={(folderId) => setCurrentFolderId(folderId)}
      onClickSubmitMove={onSubmitMoveHandler}
      onClose={onClose}
    />
  );
};
